import { data_from_server_post } from '@/shared/http/client';
import { downloadTextFile } from '@/shared/utils/downloadTextFile';

/** Escape content embedded in a Python triple-quoted string ("""..."""). */
const escapePythonTripleQuoted = (text) =>
    (text || '').replace(/\\/g, '\\\\').replace(/"""/g, '\\"\\"\\"');

/**
 * Pad embedded content with a leading/trailing space when its first/last
 * character would otherwise butt up against the surrounding string delimiter
 * (e.g. a leading or trailing `"` next to a Python `"""`), which produces an
 * invalid literal such as `""""`. Content without a collision is left untouched.
 */
const padForDelimiter = (content, delimiterChar) => {
    let padded = content;
    if (padded.startsWith(delimiterChar)) {
        padded = ` ${padded}`;
    }
    if (padded.endsWith(delimiterChar)) {
        padded = `${padded} `;
    }
    return padded;
};

const generatePythonFile = (example, taskName, projectName, serviceUrl) => {
    const escapedExample = padForDelimiter(escapePythonTripleQuoted(example || '<your-example>'), '"');

    return `from specific_ai import SpecificAIClient


def specific_ai_example():
    """Example of using SpecificAIClient for direct inference on SpecificAI models."""

    example = """${escapedExample}"""

    client = SpecificAIClient(
        url="${serviceUrl}",  # Your SpecificAI platform URL (or the inference
        #                                endpoint URL if the model is deployed independently
        #                                from the specific ai platform.
        #                                Example: http://35.124.75.132:8000)
        # trace=True,   # Uncomment to enable request tracing for SpecificAI platform
        # inference_url="<your-inference-endpoint-url>",  # Uncomment 'inference_url' only if
        #                                                   the model is deployed
        #                                                   independently from the specific ai
        #                                                   platform and you want to trace
        #                                                   its interactions within the
        #                                                   platform (trace=True).
        #                                                   Example: http://35.124.75.132:8000/public/triton
    )

    response = client.create(
        message=example,
        task_name="${taskName}",
        project_name="${projectName}",
    )

    print(f"Result: {response}")


if __name__ == "__main__":
    specific_ai_example()
`;
};

const generateTypeScriptFile = (example, taskName, projectName, serviceUrl) => {
    const escapedExample = (example || '<your-example>').replace(/\\/g, '\\\\').replace(/`/g, '\\`').replace(/\$/g, '\\$');

    return `import { SpecificAIClient } from "specific-ai"

const example = \`${escapedExample}\`

const client = new SpecificAIClient({
    URL: "${serviceUrl}", // Your SpecificAI platform URL (or the inference
    //                                endpoint URL if the model is deployed independently
    //                                from the specific ai platform.
    //                                Example: http://35.124.75.132:8000)
    // trace: true,   // Uncomment to enable request tracing for SpecificAI platform
    // inferenceURL: "<your-inference-endpoint-url>",  // Uncomment 'inferenceURL' only if
    //                                                    the model is deployed
    //                                                    independently from the specific ai
    //                                                    platform and you want to trace
    //                                                    its interactions within the
    //                                                    platform (trace=true).
    //                                                    Example: http://35.124.75.132:8000/public/triton
});

const response = await client.create(example, {
    taskName: "${taskName}",
    projectName: "${projectName}",
});

if ("labels" in response) {
    console.log("Classification result: ", response.labels);
}
if ("entities" in response) {
    console.log("Entities: ", response.entities);
}
`;
};

/**
 * Render a Go raw string literal (backtick-delimited) for the given content,
 * the idiomatic equivalent of Python triple-quoted strings. Raw literals cannot
 * contain a backtick, so any backticks are spliced in via concatenation.
 */
const toGoRawString = (text) => {
    const value = text || '<your-example>';
    return value
        .split('`')
        .map((part) => '`' + part + '`')
        .join(' + "`" + ');
};

/**
 * Build the Go snippet that prints the inference result for a known task type.
 * Returns the snippet plus whether it relies on the `inference` package (so the
 * import can be omitted when unknown to avoid an unused-import compile error).
 */
const goResultSnippet = (taskType) => {
    switch (taskType) {
        case 'ClassificationResponse':
            return {
                usesInference: true,
                body: `	result := resp.(*inference.ClassificationResponse)
	fmt.Printf("Labels: %v\\n", result.Labels)`,
            };
        case 'NERResponse':
        case 'EntityRecognitionResponse':
            return {
                usesInference: true,
                body: `	result := resp.(*inference.EntityRecognitionResponse)
	for _, e := range result.Entities {
		fmt.Printf("Entity: %s (%s)\\n", *e.Content, *e.Label)
	}`,
            };
        case 'Summarization':
        case 'ContentGeneration':
            return {
                usesInference: true,
                body: `	result := resp.(*inference.GenerationResponse)
	fmt.Printf("Response: %s\\n", result.Response)`,
            };
        default:
            return {
                usesInference: false,
                body: `	fmt.Printf("Result: %v\\n", resp)`,
            };
    }
};

const generateGoFile = (example, taskName, projectName, serviceUrl, taskType) => {
    const exampleLiteral = toGoRawString(example);
    const { usesInference, body } = goResultSnippet(taskType);
    const inferenceImport = usesInference
        ? '\n\t"github.com/marketplace-specificai/go-specific-ai-sdk/inference"'
        : '';

    return `package main

import (
	"context"
	"fmt"
	"log"

	specificai "github.com/marketplace-specificai/go-specific-ai-sdk"${inferenceImport}
)

func main() {
	client, err := specificai.NewClient(
		specificai.WithBaseURL("${serviceUrl}"), // Your SpecificAI platform URL (or the inference
		//                                                   endpoint URL if the model is deployed independently
		//                                                   from the specific ai platform.
		//                                                   Example: http://35.124.75.132:8000)
		// specificai.WithTrace(true),   // Uncomment to enable request tracing for SpecificAI platform
		// specificai.WithInferenceURL("<your-inference-endpoint-url>"),  // Uncomment 'WithInferenceURL' only if
		//                                                                  the model is deployed
		//                                                                  independently from the specific ai
		//                                                                  platform and you want to trace
		//                                                                  its interactions within the
		//                                                                  platform (WithTrace(true)).
		//                                                                  Example: http://35.124.75.132:8000/public/triton
	)
	if err != nil {
		log.Fatal(err)
	}
	defer client.Close()

	example := ${exampleLiteral}
	taskName := "${taskName}"       // Task name in the SpecificAI platform
	projectName := "${projectName}" // Project name in the SpecificAI platform

	resp, err := client.Create(context.Background(), example, taskName, projectName)
	if err != nil {
		log.Fatal(err)
	}

${body}
}
`;
};

const fetchFirstExample = (clientId, usecaseId) => {
    return new Promise((resolve) => {
        data_from_server_post(
            'dataset-records',
            {
                client_id: clientId,
                llm_usecase_id: usecaseId,
                first_index: 0,
                num_records: 1,
                is_benchmark: false,
            },
            (response) => {
                const records = response?.records || [];
                if (records.length > 0) {
                    resolve(records[0].request_example || '');
                } else {
                    resolve('');
                }
            },
            () => resolve('')
        );
    });
};

export const QUERY_FILE_LANGUAGES = [
    { key: 'python', label: 'Python' },
    { key: 'typescript', label: 'TypeScript' },
    { key: 'go', label: 'Go' },
];

/**
 * Resolve the base SDK ``url`` for a download source.
 *
 * Models page downloads target the deployed Triton ingress, so the SDK base URL
 * is just the app origin and the SDK appends
 * ``/public/triton/v2/models/.../infer`` itself.
 *
 * Playground downloads target the live GPU vLLM pod behind the
 * ``/public/playground`` ingress, which strips that prefix before the pod's
 * middleware translates the Triton-style call into ``/v1/chat/completions``.
 */
const resolveServiceUrl = ({ usePlaygroundIngress = false } = {}) =>
    usePlaygroundIngress
        ? `${window.location.origin}/public/playground`
        : `${window.location.origin}/`;

/**
 * Builds a query file (Python, TypeScript or Go) with the first dataset
 * example, task name, and project name. Returns `{ content, filename }` so
 * callers can either download it or display it (query-file code modal).
 */
export const buildQueryFile = async ({
    language,
    clientId,
    usecaseId,
    taskName,
    projectName,
    taskType,
    usePlaygroundIngress = false,
}) => {
    const example = await fetchFirstExample(clientId, usecaseId);
    const serviceUrl = resolveServiceUrl({ usePlaygroundIngress });

    const generators = {
        python: generatePythonFile,
        typescript: generateTypeScriptFile,
        go: generateGoFile,
    };
    const extensions = { python: 'py', typescript: 'ts', go: 'go' };
    const generator = generators[language] || generatePythonFile;
    const extension = extensions[language] || 'py';
    const filename = `specific_ai_query_${taskName.replace(/\s+/g, '_')}.${extension}`;

    const content = generator(example, taskName, projectName, serviceUrl, taskType);
    return { content, filename };
};

/**
 * Downloads a query file (Python, TypeScript or Go) with the first dataset
 * example, task name, and project name.
 */
export const downloadQueryFile = async (options) => {
    const { content, filename } = await buildQueryFile(options);
    downloadTextFile(content, filename);
};
